import {Button, CheckIcon, FlashIcon} from '@kit';

import React, {useCallback, useEffect, useState} from 'react';

import {
	GeneratedName,
	NameGroup,
	generateNames,
	givenGroups,
	groupsOfPeople,
	hasGenderedForms,
	GENDER_GROUPS,
	NameGender,
	peoplesOf,
	uniqueByCode,
} from './generate-names';

import css from './names-page.module.css';

const COUNT = 12;

type Props = {
	groups: NameGroup[];
};

export const NamesGenerator: React.FC<Props> = ({groups}) => {
	const peoples = peoplesOf(groups);
	const [people, setPeople] = useState<string | null>(null);
	const given = uniqueByCode(givenGroups(groupsOfPeople(groups, people)));

	const needsGender = hasGenderedForms(groups) && !given.some((group) => GENDER_GROUPS.includes(group.code));
	const [gender, setGender] = useState<NameGender | null>(null);
	const [givenId, setGivenId] = useState<string | null>(null);
	const [names, setNames] = useState<GeneratedName[]>([]);
	const [copied, setCopied] = useState<string | null>(null);

	const roll = useCallback(
		() => setNames(generateNames(groups, {givenId, people, gender, count: COUNT})),
		[groups, givenId, people, gender],
	);

	useEffect(roll, [roll]);

	const copy = (name: GeneratedName) => {
		navigator.clipboard?.writeText(name.text).then(() => {
			setCopied(name.id);
			window.setTimeout(() => setCopied((current) => (current === name.id ? null : current)), 1500);
		});
	};

	return (
		<section className={css.generator} aria-label="Генератор имён">
			<div className={css.options}>
				{peoples.length > 0 && (
					<div className={css.chips} role="group" aria-label="Народ">
						<Button size="small" shape="circular" appearance="outline" checked={people === null} onClick={() => setPeople(null)}>
							Любой народ
						</Button>
						{peoples.map((item) => (
							<Button key={item} size="small" shape="circular" appearance="outline" checked={people === item} onClick={() => setPeople(item)}>
								{item}
							</Button>
						))}
					</div>
				)}
				{given.length > 1 && (
				<div className={css.chips} role="group" aria-label="Какие имена">
					<Button size="small" shape="circular" appearance="outline" checked={givenId === null} onClick={() => setGivenId(null)}>
						Любые
					</Button>
					{given.map((group) => (
						<Button
							key={group.code}
							size="small"
							shape="circular"
							appearance="outline"
							checked={givenId === group.code}
							onClick={() => setGivenId(group.code)}
						>
							{group.title}
						</Button>
					))}
				</div>
				)}
				{needsGender && (
					<div className={css.chips} role="group" aria-label="Пол">
						<Button size="small" shape="circular" appearance="outline" checked={gender === null} onClick={() => setGender(null)}>
							Любой пол
						</Button>
						<Button size="small" shape="circular" appearance="outline" checked={gender === 'male'} onClick={() => setGender('male')}>
							Мужской
						</Button>
						<Button size="small" shape="circular" appearance="outline" checked={gender === 'female'} onClick={() => setGender('female')}>
							Женский
						</Button>
					</div>
				)}
			</div>

			<ul className={css.results}>
				{names.map((name) => (
					<li key={name.id}>
						<button type="button" className={css.result} onClick={() => copy(name)} title="Скопировать">
							<span className={css.resultName}>
								{name.parts.map((part, index) => (
									<span key={index} className={part.kind === 'given' ? undefined : css[`result${part.kind === 'family' ? 'Family' : 'Nickname'}`]}>
										{index > 0 && ' '}
										{part.text}
									</span>
								))}
							</span>
							{name.note && <span className={css.resultNote}>{name.note}</span>}
							{copied === name.id && <CheckIcon className={css.resultCopied} width={16} height={16} />}
						</button>
					</li>
				))}
			</ul>

			<Button appearance="primary" icon={<FlashIcon />} onClick={roll}>
				Сгенерировать ещё
			</Button>
		</section>
	);
};
